import React from "react";
import { Line, Bar } from "react-chartjs-2";

const THEME = {
  primary: "#FB565F",
  primaryLight: "#fecdd3",
  dark: "#1f2937",
  success: "#10b981",
  info: "#3b82f6",
  warning: "#f59e0b",
  danger: "#dc2626",
};

const LINE_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { position: "top" } },
  scales: { x: { grid: { display: false } }, y: { beginAtZero: true } },
};

const BAR_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { position: "top" } },
  scales: {
    x: { grid: { display: false } },
    y: { beginAtZero: true },
  },
};

const MONTH_NAMES_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function DashboardCharts({ weekGenderDistribution, monthlyUserStats, subscriptionTrend }) {
  const weekLabels = (weekGenderDistribution || []).map((d) => d.day);
  const weeklyChart = {
    labels: weekLabels,
    datasets: [
      {
        label: "Male",
        data: (weekGenderDistribution || []).map((d) => d.males),
        borderColor: THEME.primary,
        backgroundColor: "transparent",
        fill: false,
        tension: 0.3,
      },
      {
        label: "Female",
        data: (weekGenderDistribution || []).map((d) => d.females),
        borderColor: THEME.dark,
        backgroundColor: "transparent",
        fill: false,
        tension: 0.3,
      },
    ],
  };

  const monthlyLabels = (monthlyUserStats || []).map((d) => `${MONTH_NAMES_SHORT[Number(d.month) - 1]} ${d.year}`);
  const monthlyCounts = (monthlyUserStats || []).map((d) => d.count ?? 0);
  const monthlyChart = {
    labels: monthlyLabels.length ? monthlyLabels : ["—"],
    datasets: [
      {
        label: "New users",
        data: monthlyCounts,
        borderColor: THEME.info,
        backgroundColor: "transparent",
        fill: false,
        tension: 0.3,
      },
    ],
  };

  const subLabels = (subscriptionTrend || []).map((d) => `${MONTH_NAMES_SHORT[Number(d.month) - 1]} ${d.year}`);
  const subscriptionChart = {
    labels: subLabels.length ? subLabels : ["—"],
    datasets: [
      {
        label: "New",
        data: (subscriptionTrend || []).map((d) => d.new ?? 0),
        backgroundColor: THEME.success,
      },
      {
        label: "Expired",
        data: (subscriptionTrend || []).map((d) => d.expired ?? 0),
        backgroundColor: THEME.warning,
      },
      {
        label: "Canceled",
        data: (subscriptionTrend || []).map((d) => d.canceled ?? 0),
        backgroundColor: THEME.danger,
      },
    ],
  };

  return (
    <div className="mb-6 lg:mb-8">
      <h2 className="font-main text-lg sm:text-xl font-bold text-gray-900 mb-4">Charts</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl shadow-lg border-2 p-5 min-w-0" style={{ borderColor: THEME.primaryLight }}>
          <div className="text-center font-main font-bold text-gray-800 mb-2">Weekly New Users</div>
          <div className="min-h-[220px] sm:h-64 w-full max-w-full">
            <Line data={weeklyChart} options={LINE_OPTIONS} />
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-lg border-2 p-5 min-w-0" style={{ borderColor: THEME.primaryLight }}>
          <div className="text-center font-main font-bold text-gray-800 mb-2">Monthly New Users (Last 12 Months)</div>
          <div className="min-h-[220px] sm:h-64 w-full max-w-full">
            <Line data={monthlyChart} options={LINE_OPTIONS} />
          </div>
        </div>
        {subscriptionTrend && subscriptionTrend.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg border-2 p-5 min-w-0 lg:col-span-2" style={{ borderColor: THEME.primaryLight }}>
            <div className="text-center font-main font-bold text-gray-800 mb-2">Subscription Trend (Last 12 Months)</div>
            <div className="min-h-[220px] sm:h-64 w-full max-w-full">
              <Bar data={subscriptionChart} options={BAR_OPTIONS} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
